'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/lib/auth-store';
import { ChevronDown, User, LogOut, Settings, LayoutDashboard, QrCode } from 'lucide-react';
import { getEffectiveRole, isAdminRole, isJuryRole, isOrganizerRole } from '@/lib/rbac';
import { resolveDashboardForUser } from '@/lib/role-dashboard';
import { startNavigationLoading } from '@/lib/navigation-loading';

export function UserNav() {
  const router = useRouter();
  const { user, logout } = useAuthStore();
  const [isOpen, setIsOpen] = React.useState(false);

  if (!user) return null;

  const role = getEffectiveRole(user);
  const adminRole = isAdminRole(role);
  const organizerRole = isOrganizerRole(role);
  const juryRole = isJuryRole(role);
  const dashboardHref = resolveDashboardForUser(user);

  const navigate = (href: string) => {
    startNavigationLoading();
    router.push(href);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="group flex items-center gap-3 rounded-xl px-3 py-2 transition-all duration-300 hover:bg-black/5"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-black/5 bg-[#cad6d6] text-soot/60 shadow-sm transition-transform group-hover:scale-105">
          {user.avatar ? (
            <img src={user.avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            <User className="h-5 w-5" aria-hidden />
          )}
        </div>
        <div className="hidden text-left sm:block">
          <p className="mb-1 text-sm font-bold leading-none text-soot/90">
            {user.name || 'Account'}
          </p>
          <p className="text-[10px] font-black uppercase tracking-widest text-soot/30">
            {role || 'Member'}
          </p>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-soot/30 transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-black/5 bg-bg shadow-2xl duration-200 animate-in fade-in zoom-in-95"
          >
            <div className="border-b border-black/5 bg-bg/50 p-4">
              <p className="mb-1 text-xs font-black uppercase tracking-widest text-soot/30">Signed in as</p>
              <p className="truncate text-sm font-bold text-soot">{user.email}</p>
            </div>
            <div className="p-2">
              <button
                type="button"
                role="menuitem"
                onClick={() => navigate(dashboardHref)}
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-soot/70 transition-all hover:bg-black/5 hover:text-soot"
              >
                <LayoutDashboard className="h-4 w-4 text-soot/20 group-hover:text-soot/40" aria-hidden />
                {adminRole || organizerRole ? 'Dashboard' : 'Overview'}
              </button>
              {!adminRole && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => navigate('/dashboard/profile')}
                  className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-soot/70 transition-all hover:bg-black/5 hover:text-soot"
                >
                  <User className="h-4 w-4 text-soot/20 group-hover:text-soot/40" aria-hidden />
                  Profile
                </button>
              )}
              {adminRole && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => navigate('/admin/profile')}
                  className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-soot/70 transition-all hover:bg-black/5 hover:text-soot"
                >
                  <User className="h-4 w-4 text-soot/20 group-hover:text-soot/40" aria-hidden />
                  Profile
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => navigate('/dashboard/settings')}
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-soot/70 transition-all hover:bg-black/5 hover:text-soot"
              >
                <Settings className="h-4 w-4 text-soot/20 group-hover:text-soot/40" aria-hidden />
                Settings
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={async () => {
                  try {
                    await logout();
                  } catch (err) {
                    console.error('Logout failed:', err);
                  } finally {
                    setIsOpen(false);
                    window.location.href = '/login?reason=logged_out';
                  }
                }}
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-red-500 transition-all hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 text-red-300 group-hover:text-red-500" aria-hidden />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
